import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../../Context/ThemeContext';
import { useLanguage } from '../../../Context/LanguageContext';
import { getColor, lightTheme, darkTheme } from '../../../Styles/theme';

import { DndContext, DragOverlay, closestCenter, pointerWithin, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import useDndAutoScroll from '../../Share/Normal/useDndAutoScroll';

import FetchModule from '../../Share/Network/FetchModule';

import EmptyValueLogo from '../../../image/EmptyValueLogo.png';
import { ReactComponent as PlusSvgIcon } from '../../../image/Plus.svg';
import MemberShipInfoBox from './MemberShipInfoBox';
import MemberShipEditBox from './MemberShipEditBox';
import DeleteConfirmModal from './Modal/DeleteConfirmModal';
import CustomAlert from '../../Share/Normal/CustomAlert';
import CategoryBox from './CategoryBox';
import CategoryEditBox from './CategoryEditBox';

export default function NewMemberShipAndClassView() {
  const history = useHistory();
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const theme = isDark ? darkTheme : lightTheme;

  const [isConfirm, setIsConfirm] = useState(false);
  const [isViewCustomAlert, setIsViewCustomAlert] = useState(false);
  const [alertText, setAlertText] = useState('');
  const [rendering, setRendering] = useState(false);

  const [myStore, setMyStore] = useState({
    storeId: 0,
    storeName: '',
    storeLogo: '',
  })
  const [followStores, setFollowStores] = useState([]);
  const [memberShipData, setMemberShipData] = useState([]);
  const [useMemberShipData, setUseMemberShipData] = useState([]);
  const [memberShipBodyHeight, setMemberShipBodyHeight] = useState(0);
  const [lessonData, setLessonData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);
  const [isDragOverCategory, setIsDragOverCategory] = useState(false);

  const [isClickedLessonEdit, setIsClickedLessonEdit] = useState(false);
  const [isClickedMemberShipEdit, setIsClickedMemberShipEdit] = useState(false);
  const [isCategoryEditReady, setIsCategoryEditReady] = useState(false);
  const [isMemberShipEditReady, setIsMemberShipEditReady] = useState(false);

  const [clickLessonData, setClickLessonData] = useState(undefined);
  const [clickEditLessonData, setClickEditLessonData] = useState(undefined);
  const [clickEditMemberShipData, setClickEditMemberShipData] = useState(undefined);

  // 수업 추가하기 클릭시 이미 클릭했던 수업 정보 담기 //
  const [tempClickLessonData, setTempClickLessonData] = useState(undefined);

  const [toBeDeletedDivision, setToBeDeletedDivision] = useState('None');
  const [toBeDeletedData, setToBeDeletedData] = useState(undefined);

  const [isInProgress, setIsInProgress] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );
  const { scrollContainerRef, onDragMove, stopAutoScroll } = useDndAutoScroll();
  const { scrollContainerRef: membershipScrollRef, onDragMove: onMembershipDragMove, stopAutoScroll: stopMembershipAutoScroll } = useDndAutoScroll();

  // Init API Call //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('membership/', 'GET');

        if (responseData.status === 200) {
          setMyStore(responseData.data.myStoreData);
          setFollowStores(responseData.data.followStores);
          setMemberShipData(responseData.data.memberShips);
          setCategoryData(responseData.data.categories);
          setLessonData(responseData.data.lessonInfos);
        }
        else if (responseData.status === 401) history.push('/');
      }
    )();
  }, [rendering])

  // 수업 클릭시 회원권 내역 찾기 및 회원권 뷰 크기 계산 //
  useEffect(() => {
    if (clickLessonData === undefined) {
      return;
    }

    setIsClickedLessonEdit(false);

    let finalData = [];

    for (let i = 0; i < memberShipData.length; i++) {
      if (memberShipData[i].categoryId === clickLessonData.id) {
        finalData.push(memberShipData[i])
      }
    }
    setUseMemberShipData(finalData);

    if (finalData.length === 0) setMemberShipBodyHeight(60);
    else {
      const scrollHeight = Math.min(finalData.length * 55, 440);
      setMemberShipBodyHeight(scrollHeight + 80);
    }
  }, [clickLessonData, memberShipData])



  // CategoryEditBox 애니메이션(300ms) 동안만 overflow hidden, 완료 후 visible //
  useEffect(() => {
    setIsCategoryEditReady(false);
    const timer = setTimeout(() => setIsCategoryEditReady(true), 300);
    return () => clearTimeout(timer);
  }, [isClickedLessonEdit]);

  // MemberShipEditBox 열릴 때: 300ms 후 visible / 닫힐 때: 즉시 hidden //
  useEffect(() => {
    if (isClickedMemberShipEdit) {
      setIsMemberShipEditReady(false);
      const timer = setTimeout(() => setIsMemberShipEditReady(true), 300);
      return () => clearTimeout(timer);
    } else {
      setIsMemberShipEditReady(false);
    }
  }, [isClickedMemberShipEdit]);

  // 수업 추가하기 클릭시 //
  function onClickAddLessonEdit() {
    setTempClickLessonData(clickLessonData);
    setClickLessonData(undefined);
    setClickEditLessonData(undefined);
    setIsClickedLessonEdit(true);
  }

  // 수업 추가하기 닫기 클릭시 //
  function onClickExitAddLessonEdit() {
    setClickLessonData(tempClickLessonData);
    setTempClickLessonData(undefined);
    setClickEditLessonData(undefined);
    setIsClickedLessonEdit(false);
  }

  // 수업 수정 클릭시 //
  function onClickEditLessonButton(data) {
    setClickEditLessonData(data);
    setTempClickLessonData(clickLessonData);
    setClickLessonData(undefined);
    setIsClickedLessonEdit(true);
  }

  // 회원권 추가하기 클릭시 //
  function onClickMemberShipEdit() {
    setClickEditMemberShipData(undefined);
    setIsClickedMemberShipEdit(true);
  }

  // 회원권 클릭시 ( 수정 겸용 ) //
  function onClickEditMemberShipButton(data) {
    setClickEditMemberShipData(data);
    setIsClickedMemberShipEdit(true);
  }

  // 회원권 추가하기 닫기 클릭시 //
  function onClickExitAddMemberShipEdit() {
    setClickEditMemberShipData(undefined);
    setIsClickedMemberShipEdit(false);
    if (membershipScrollRef.current) membershipScrollRef.current.scrollTop = 0;
  }

  // 회원권 저장 클릭시 //
  function onSaveMemberShip(requestData) {
    if (isProcessing) {
      alert(t('settingMembership.savingInProgress'));
      return;
    }

    setIsProcessing(true);

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('membership/save', 'POST', requestData);

        if (responseData.status === 200) {
          let memberShip = memberShipData.slice();

          let isChanged = false;

          for (let i = 0; i < memberShip.length; i++) {
            if (memberShip[i].id === responseData.data.id) {
              memberShip[i] = responseData.data;
              isChanged = true;
              break;
            }
          }

          if (!isChanged) memberShip.push(responseData.data);

          setMemberShipData(memberShip);
          onClickExitAddMemberShipEdit();
          setIsProcessing(false);

          return true;
        }
        else if (responseData.status === 201) {
          alert(t('settingMembership.duplicateName'));
          setIsProcessing(false);

          return false;
        }
      }
    )();
  }

  // 수업 저장 클릭시 //
  function onSaveLessonInfo(requestData) {
    if (isProcessing) {
      alert(t('settingMembership.savingInProgress'));
      return;
    }

    setIsProcessing(true);

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postData('membership/category/save', 'POST', requestData);

        if (responseData.status === 200) {
          let category = categoryData.slice();

          let isChanged = false;

          for (let i = 0; i < category.length; i++) {
            if (category[i].id === responseData.data.id) {
              category[i] = responseData.data;
              isChanged = true;
              break;
            }
          }

          if (!isChanged) category.push(responseData.data);

          setCategoryData(category);
          onClickExitAddLessonEdit();
        }

        setIsProcessing(false);
      }
    )();
  }

  // 회원권 삭제 클릭시 //
  function onClickDeletedMemberShip(data) {
    setIsConfirm(true);
    setToBeDeletedDivision('MemberShip');
    setToBeDeletedData(data);
  }

  // 수업 삭제 클릭시 //
  function onClickDeletedLesson(data) {
    setIsConfirm(true);
    setToBeDeletedDivision("Category");
    setToBeDeletedData(data);
  }

  // 삭제 취소 클릭시
  function deleteExitButtonClick() {
    setToBeDeletedDivision('None');
    setToBeDeletedData(undefined);
    setIsConfirm(false);
  }

  // 삭제 확인 버튼 클릭시
  function deleteYesClick() {
    if (toBeDeletedDivision === 'Category') {
      let requestData = {
        categoryId: toBeDeletedData.id
      }
      
      const fetchModule = new FetchModule();
      (
        async () => {
          const responseData = await fetchModule.postData('membership/category/delete', 'POST', requestData);

          if (responseData.status === 200) setRendering(!rendering);
          else if (responseData.status === 202) {
            setAlertText(t('settingMembership.linkedMembershipError'));
            setIsViewCustomAlert(true);
          }
          else if (responseData.status === 401) history.push('/');
        }
      )();
    }
    else if (toBeDeletedDivision === 'MemberShip') {
      let requestData = {
        memberShipId: toBeDeletedData.id
      }

      const fetchModule = new FetchModule();
      (
        async () => {
          const responseData = await fetchModule.postDataVersion2('membership/delete/', 'POST', requestData);

          if (responseData.status === 200) {
            if (clickEditMemberShipData?.id === toBeDeletedData.id) onClickExitAddMemberShipEdit();
            setRendering(!rendering);
          }
          else if (responseData.status === 203) {
            setAlertText(t('settingMembership.activeMembershipError'));
            setIsViewCustomAlert(true);
          }
          else if (responseData.status === 204) {
            setAlertText(t('settingMembership.deleteFailed'));
            setIsViewCustomAlert(true);
          }
          else if (responseData.status === 401) history.push('/');
        }
      )();
    }
    setIsConfirm(false);
  }

  // 회원권 복사 클릭시 //
  function onClickCopyMemberShip(data) {
    if (isProcessing) return;

    setIsProcessing(true);

    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.postDataVersion2('membership/copy', 'POST', { membershipId: data.id });
      if (responseData.status === 200) {
        setRendering(prev => !prev);
      } else if (responseData.status === 201) {
        alert(t('settingMembership.duplicateName'));
      }
      setIsProcessing(false);
    })();
  }

  // cat-* / mem-* 접두사 충돌 방지용 collision detection //
  function customCollisionDetection(args) {
    const activeId = args.active.id.toString();
    if (activeId.startsWith('cat-')) {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(c => c.id.toString().startsWith('cat-'))
      });
    }
    // 회원권 드래그: 편집 박스가 열려 있으면 카테고리로 이동 불가
    if (!isClickedMemberShipEdit) {
      const rect = scrollContainerRef.current?.getBoundingClientRect();
      if (rect && args.pointerCoordinates) {
        const { x, y } = args.pointerCoordinates;
        const isInLessonBox = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
        if (isInLessonBox) {
          const catContainers = args.droppableContainers.filter(c => c.id.toString().startsWith('cat-'));
          // 포인터가 카테고리 박스 위에 직접 있으면 즉시 감지, 갭이면 가장 가까운 카테고리 반환
          const direct = pointerWithin({ ...args, droppableContainers: catContainers });
          if (direct.length > 0) return direct;
          return closestCenter({ ...args, droppableContainers: catContainers });
        }
      }
    }
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter(c => c.id.toString().startsWith('mem-'))
    });
  }

  // 드래그 시작시 //
  function onDragStart(event) {
    setActiveDragId(event.active.id);
    setIsDragOverCategory(false);
  }

  // 드래그 대상이 변경될 때 DragBadge 표시 여부 결정 //
  function onDragOverAll(event) {
    if (!event.active.id.toString().startsWith('mem-')) return;
    setIsDragOverCategory(!!event.over?.id?.toString().startsWith('cat-'));
  }

  // 드래그 이동시 자동 스크롤 라우팅 //
  function onDragMoveAll(event) {
    if (event.active.id.toString().startsWith('cat-')) {
      onDragMove(event);
    } else {
      const rect = scrollContainerRef.current?.getBoundingClientRect();
      if (rect) {
        const x = event.activatorEvent.clientX + event.delta.x;
        const y = event.activatorEvent.clientY + event.delta.y;
        const isInLessonBox = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
        if (isInLessonBox) onDragMove(event);
        else onMembershipDragMove(event);
      } else {
        onMembershipDragMove(event);
      }
    }
  }

  // 드래그 완료시 (카테고리 정렬 / 회원권 정렬 / 회원권 카테고리 이동) //
  function onDragEndAll(event) {
    setActiveDragId(null);
    setIsDragOverCategory(false);
    stopAutoScroll();
    stopMembershipAutoScroll();
    const { active, over } = event;
if (!over || active.id === over.id) return;

    const activeId = active.id.toString();
    const overId = over.id.toString();

    if (activeId.startsWith('cat-') && overId.startsWith('cat-')) {
      const realActiveId = parseInt(activeId.replace('cat-', ''));
      const realOverId = parseInt(overId.replace('cat-', ''));
      const oldIndex = categoryData.findIndex(d => d.id === realActiveId);
      const newIndex = categoryData.findIndex(d => d.id === realOverId);
      setCategoryData(arrayMove(categoryData, oldIndex, newIndex));
      const fetchModule = new FetchModule();
      (async () => {
        const responseData = await fetchModule.postData('membership/category/change/order/', 'POST', {
          categoryId: realActiveId,
          orderNum: newIndex + 1
        });
        if (responseData.status === 200) setRendering(prev => !prev);
      })();

    } else if (activeId.startsWith('mem-') && overId.startsWith('mem-')) {
      const realActiveId = parseInt(activeId.replace('mem-', ''));
      const realOverId = parseInt(overId.replace('mem-', ''));
      const oldIndex = useMemberShipData.findIndex(d => d.id === realActiveId);
      const newIndex = useMemberShipData.findIndex(d => d.id === realOverId);
      setUseMemberShipData(arrayMove(useMemberShipData, oldIndex, newIndex));
      const fetchModule = new FetchModule();
      (async () => {
        const responseData = await fetchModule.postDataVersion2('membership/change/order/', 'POST', {
          membershipId: realActiveId,
          orderNum: newIndex + 1
        });
        if (responseData.status === 200) setRendering(prev => !prev);
      })();

    } else if (activeId.startsWith('mem-') && overId.startsWith('cat-')) {
      const membershipId = parseInt(activeId.replace('mem-', ''));
      const newCategoryId = parseInt(overId.replace('cat-', ''));
      const membership = memberShipData.find(m => m.id === membershipId);
      if (!membership || membership.categoryId === newCategoryId || isProcessing) return;
      setIsProcessing(true);
      const requestData = {
        id: membership.id,
        lessonId: 0,
        categoryId: newCategoryId,
        storeId: 0,
        name: membership.name,
        price: membership.price,
        validity: membership.validity,
        validityUnit: membership.validityUnit,
        availableReserveLessonIds: membership.availableReserveLessonIds,
        totalMax: membership.totalMax,
        weekMax: membership.weekMax,
        weekCountMax: membership.weekCountMax,
        dayMax: membership.dayMax,
        availableHold: membership.availableHold,
        holdCount: membership.holdCount,
        autoPayment: membership.autoPayment,
        isHideValidity: membership.isHideValidity,
        isHidePrice: membership.isHidePrice,
        isHideAll: membership.isHideAll,
        partnerGymAvailableLessons: membership.partnerGymAvailableLessons,
        isUseIndividualLessonReserveOption: membership.isUseIndividualLessonReserveOption,
        availableReserveLessonOptions: membership.availableReserveLessonOptions,
        usePartnerGymIndividualLessonReserveOptions: membership.usePartnerGymIndividualLessonReserveOptions,
        partnerGymAvailableReserveLessonOptions: membership.partnerGymAvailableReserveLessonOptions
      };
      const fetchModule = new FetchModule();
      (async () => {
        const responseData = await fetchModule.postDataVersion2('membership/save', 'POST', requestData);

if (responseData.status === 200) {
          setMemberShipData(prev => prev.map(m => m.id === responseData.data.id ? responseData.data : m));
        }
        setIsProcessing(false);
      })();
    }
  }

  // 인덱스 변경 Up Down 버튼 클릭시 //
  function onClickChangeCategoryOrderIndex(division, index) {
    if (isInProgress) return;

    setIsInProgress(true);

    let changeIndex = division === 'UP' ? categoryData[index].orderNum - 1 : categoryData[index].orderNum + 1;

    let requestData = {
      categoryId: categoryData[index].id,
      orderNum: changeIndex
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postData('membership/category/change/order/', 'POST', requestData);

        if (responseData.status === 200) {
          setRendering(!rendering);
          setIsInProgress(false);
        }
        else setIsInProgress(false);
      }
    )();
  }

  // 회원권 드래그 앤 드롭 순서 변경 //
  function onDragEndMembership(event) {
    stopMembershipAutoScroll();
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = useMemberShipData.findIndex(d => d.id === active.id);
    const newIndex = useMemberShipData.findIndex(d => d.id === over.id);

    setUseMemberShipData(arrayMove(useMemberShipData, oldIndex, newIndex));

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('membership/change/order/', 'POST', {
          membershipId: active.id,
          orderNum: newIndex + 1
        });

        if (responseData.status === 200) setRendering(prev => !prev);
      }
    )();
  }

  // 인덱스 변경 Up Down 버튼 클릭시 //
  function onClickChangeMembershipOrderIndex(division, index) {
    if (isInProgress) return;

    setIsInProgress(true);

    let changeIndex = division === 'UP' ? useMemberShipData[index].orderNum - 1 : useMemberShipData[index].orderNum + 1;

    let requestData = {
      membershipId: useMemberShipData[index].id,
      orderNum: changeIndex
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('membership/change/order/', 'POST', requestData);

        if (responseData.status === 200) {
          // 순서 변경 응답에는 Transient 필드가 없으므로 orderNum만 머지 //
          const updatedOrderMap = {};

          for (const m of responseData.data.memberships) {
            updatedOrderMap[m.id] = m.orderNum;
          }

          setMemberShipData(prev => {
            const merged = prev.map(m =>
              updatedOrderMap[m.id] !== undefined
                ? { ...m, orderNum: updatedOrderMap[m.id] }
                : m
            );
            // orderNum 정렬: useMemberShipData가 categoryId 필터만 수행하므로 표시 순서 보장 위해 정렬 //
            return merged.sort((a, b) => a.orderNum - b.orderNum);
          });
          setIsInProgress(false);
        }
        else setIsInProgress(false);
      }
    )();
  }

  return (
    <DndContext sensors={sensors} collisionDetection={customCollisionDetection} onDragStart={onDragStart} onDragOver={onDragOverAll} onDragEnd={onDragEndAll} onDragMove={onDragMoveAll} autoScroll={false}>
      <TopLevelWrapper>
        {isViewCustomAlert && <CustomAlert text={alertText} closeFn={() => setIsViewCustomAlert(false)} />}
        {isConfirm && <DeleteConfirmModal title={toBeDeletedDivision === 'Category' ? t('settingMembership.deleteCategory') : t('settingMembership.deleteMembership')} itemName={toBeDeletedData.name + (toBeDeletedDivision === 'Category' ? t('settingMembership.categorySuffix') : t('settingMembership.membershipSuffix'))} clickNoFn={deleteExitButtonClick} clickYesFn={deleteYesClick} />}
        <InfoBoxWrapper>
          <LessonBoxTotalWrapper isClickedLessonEdit={isClickedLessonEdit}>
            {
              <LessonBoxInnerWrapper isClickedMemberShipEdit={isClickedMemberShipEdit}>
                <LessonBoxWrapper ref={scrollContainerRef}>
                  <SortableContext items={categoryData.map(d => `cat-${d.id}`)} strategy={verticalListSortingStrategy}>
                    {
                      categoryData.map((data, index) => (
                        <CategoryBox
                          key={'MembershipCategories-' + data.id}
                          data={data}
                          index={index}
                          isSelected={clickLessonData?.id === data.id}
                          onClickChangeIndex={onClickChangeCategoryOrderIndex}
                          setClickLessonData={setClickLessonData}
                          isClickedLessonEdit={isClickedLessonEdit}
                          onClickEditLessonButton={onClickEditLessonButton}
                          onClickDeletedLesson={onClickDeletedLesson} />
                      ))
                    }
                  </SortableContext>
                </LessonBoxWrapper>
                <AddLessonButton onClick={onClickAddLessonEdit}>
                  <TextWrapper>
                    <StyledPlusIcon style={{ color: theme.text }} />
                    <Text fontFmaily='NotoSansKR-Medium' fontSize={14} cursor='pointer' color={theme.text} >{t('settingMembership.addCategory')}</Text>
                  </TextWrapper>
                </AddLessonButton>
              </LessonBoxInnerWrapper>
            }
            <MemberShipEditBoxClipWrapper isActive={isClickedMemberShipEdit} isReady={isMemberShipEditReady}>
              <MemberShipEditBoxOffsetWrapper>
                <MemberShipEditBox
                  myStore={myStore}
                  followStores={followStores}
                  lessonData={lessonData}
                  clickLessonData={clickLessonData}
                  clickEditMemberShipData={clickEditMemberShipData}
                  isClickedMemberShipEdit={isClickedMemberShipEdit}
                  onClickExitAddMemberShipEdit={onClickExitAddMemberShipEdit}
                  onSaveMemberShip={onSaveMemberShip} />
              </MemberShipEditBoxOffsetWrapper>
            </MemberShipEditBoxClipWrapper>
          </LessonBoxTotalWrapper>
          <MemberShipBoxTotalWrapper isReady={isCategoryEditReady}>
            <EmptyMemberShipBoxWrapper isHiding={clickLessonData !== undefined || isClickedLessonEdit}>
              <EmptyLogoView src={EmptyValueLogo} />
            </EmptyMemberShipBoxWrapper>
            {
              clickLessonData !== undefined &&
                <MemberShipBoxWrapper isClickedMemberShipEdit={isClickedMemberShipEdit} $isDark={isDark}>
                  <MemberShipBoxTitle>
                    <Text fontFmaily='NotoSansKR-Bold' fontSize={14} color={theme.text} cursor='default' style={{ maxWidth: 'calc(100% - 24px)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{clickLessonData.name}</Text>
                  </MemberShipBoxTitle>
                  <MemberShipBoxBody height={memberShipBodyHeight}>
                    <MemberShipScrollBox ref={membershipScrollRef} isExist={useMemberShipData.length > 0}>
                      <SortableContext items={useMemberShipData.map(d => `mem-${d.id}`)} strategy={verticalListSortingStrategy}>
                        {
                          useMemberShipData.map((data, index) => (
                            <MemberShipInfoBox
                              key={'MembershipView-' + data.id}
                              data={data}
                              index={index}
                              isClickedMemberShipEdit={isClickedMemberShipEdit}
                              onClickEditMemberShipButton={onClickEditMemberShipButton}
                              onClickDeletedMemberShip={onClickDeletedMemberShip}
                              onClickDeletedLesson={onClickDeletedLesson}
                              onClickChangeIndex={onClickChangeMembershipOrderIndex}
                              onClickCopyMemberShip={onClickCopyMemberShip}/>
                          ))
                        }
                      </SortableContext>
                    </MemberShipScrollBox>
                    <AddMemberShipButton $hasItems={useMemberShipData.length > 0} onClick={onClickMemberShipEdit}>
                      <TextWrapper>
                        <StyledPlusIcon style={{ color: theme.text }} />
                        <Text fontFmaily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='pointer'>{t('settingMembership.addMembership')}</Text>
                      </TextWrapper>
                    </AddMemberShipButton>
                  </MemberShipBoxBody>
                </MemberShipBoxWrapper>
            }
            <CategoryEditBox
              clickEditLessonData={clickEditLessonData}
              isClickedLessonEdit={isClickedLessonEdit}
              onClickExitAddLessonEdit={onClickExitAddLessonEdit}
              onSave={onSaveLessonInfo} />
          </MemberShipBoxTotalWrapper>
        </InfoBoxWrapper>
      </TopLevelWrapper>
      {isDragOverCategory && activeDragId?.toString().startsWith('mem-') && (() => {
        const membershipId = parseInt(activeDragId.toString().replace('mem-', ''));
        const membership = memberShipData.find(m => m.id === membershipId);
        return membership ? (
          <DragOverlay dropAnimation={null}>
            <DragBadge>{membership.name}</DragBadge>
          </DragOverlay>
        ) : null;
      })()}
    </DndContext>
  )
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  box-sizing: border-box;
  width: 1055px;

  margin-top: 20px;
  margin-bottom: 96px;

  transition: all 0.3s ease-in-out;
`;

const InfoBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
`;

const LessonBoxTotalWrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 560px;

  transition: all 0.3s ease-in-out;
`;

const MemberShipEditBoxClipWrapper = styled.div`
  position: absolute;
  left: -40px;
  top: -40px;

  padding: 20px;

  width: 100%;

  border-radius: 10px;

  padding-bottom: 50px;

  overflow: ${props => props.isReady ? 'visible' : 'hidden'};

  z-index: 10;

  pointer-events: ${props => props.isActive ? 'auto' : 'none'};
`;

const MemberShipEditBoxOffsetWrapper = styled.div`
  margin-left: 510px;
`;

const LessonBoxInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-left: -10px;

  width: 100%;

  z-index: ${props => props.isClickedMemberShipEdit ? `-3` : `1`};
  opacity: ${props => props.isClickedMemberShipEdit ? `0` : `1`};

  transition: all 0.3s ease-in-out;
`;

const LessonBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding-left: 10px;
  padding-bottom: 10px;

  width: 523.5px;
  max-height: 512px;

  gap: 10px;

  overflow-y: scroll;
  overflow-x: hidden;

	&::-webkit-scrollbar {
		width: 12px;
	}

	&::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 10px 20px ${props => props.theme.scrollBar};
		border: 0px solid transparent;
		border-left-width: 10px;
		height: 25px;
		border-radius: 200px;
	}

	&::-webkit-scrollbar-track {
    box-shadow: inset 0 0 10px 10px transparent;
		border: 4px solid transparent;
		background-color: transparent;
	}

	&::-webkit-scrollbar-track-piece {
		background-color: transparent;
	}
`;

const AddLessonButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-top: 15px;
  margin-left: 12px;

  width: 495px;
  height: 52px;

  color: ${props => props.theme.subText};

  border-radius: 12px;
  background-color: ${props => props.theme.subBackground};
  box-shadow: 0px 10px 10px rgba(0, 54, 71, 0.02);

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 4px;
`;

const MemberShipBoxTotalWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  position: relative;

  width: 522px;
  min-width: 522px;
  max-width: 522px;
  height: 593px;

  overflow: ${props => props.isReady ? 'visible' : 'hidden'};
`;

const MemberShipBoxWrapper = styled.div`
  position: absolute;

  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  min-width: 511.5px;
  max-width: 511.5px;

  border-radius: 0px 0px 10px 10px;
  box-shadow: ${props => props.theme.shadowLarge};
  transition: all 0.3s ease-in-out;
`;

const EmptyMemberShipBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 511.5px;
  min-width: 511.5px;
  max-width: 511.5px;
  height: 593px;

  opacity: ${props => props.isHiding ? 0 : 1};
  transition: opacity 0.3s ease-in-out;
`;

const MemberShipBoxTitle = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 10px;

  box-sizing: border-box;
  width: 100%;
  height: 53px;

  border-radius: 10px 10px 0px 0px;
  background-color: ${props => props.theme.primaryAlpha20};

  transition: all 0.3s ease-in-out;
`;

const MemberShipBoxBody = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  color: ${props => props.theme.subText};

  padding: 20px 0 0 0;

  width: 100%;
  height: ${props => props.height}px;

  overflow: hidden;

  transition: all 0.3s ease-in-out;

  border-radius: 0px 0px 10px 10px;
  background-color: ${props => props.theme.subBackground};
`;

const MemberShipScrollBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  max-height: 440px;

  overflow-y: scroll;
  overflow-x: hidden;

  transition: all 0.3s ease-in-out;

	&::-webkit-scrollbar {
		width: 9px;
	}

	&::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 10px 20px ${props => props.theme.scrollBar};
		border: 3px solid transparent;
		height: 25px;
		border-radius: 200px;
	}

	&::-webkit-scrollbar-track {
    box-shadow: inset 0 0 10px 10px transparent;
		border: 4px solid transparent;
		background-color: transparent;
	}

	&::-webkit-scrollbar-track-piece {
		background-color: transparent;
	}
`;

const AddMemberShipButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-top: ${props => props.$hasItems ? '20px' : '0px'};

  width: 100%;
  height: 60px;

  border-radius: 12px;
  background-color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const Text = styled.div`
  font-family: ${props => props.fontFmaily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;

const EmptyLogoView = styled.img`
  width: 180px;
  height: 180px;

  margin-bottom: 20px;
`;

const ImageWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 6px;
`;

const StyledPlusIcon = styled(PlusSvgIcon)`
  width: 20px;
  height: 20px;

  transition: color 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const DragBadge = styled.div`
  display: inline-flex;
  align-items: center;

  padding: 6px 14px;

  max-width: 240px;

  border-radius: 8px;
  background-color: ${props => props.theme.primary};

  font-family: NotoSansKR-Medium;
  font-size: 13px;
  color: ${props => props.theme.subBackground};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  pointer-events: none;
`;
